import React from 'react';
import { useSelector } from 'react-redux';
import AuthTemplateContent from './AuthTemplateContent';
import RegularTemplateContent from './RegularTemplateContent';
import { parseButtons, getMediaTypeFromComponents } from './TemplateUtils';

export const TemplatePreview = ({ data = {}, mode = 'create' }) => {
  const selectedPhone = useSelector(state => state.whatsapp?.selectedPhone);
  const displayName = selectedPhone?.displayName;
  const displayPhoneNumber = selectedPhone?.displayPhoneNumber;
  const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const {
    headerType = 'none',
    headerText = '',
    bodyText = '',
    footerText = '',
    sampleValues = {},
    mediaType = 'none',
    mediaPreview = '',
    components = null,
    isAuthTemplate = false,
    codeExpirationMinutes = 10,
    otpType = 'copy_code',
    otpButtonText,
    addSecurityRecommendation = false,
    code = sampleValues?.code || ''
  } = data || {};

  // Extract variables from components if not present in top-level data
  let parsedBodyText = bodyText;
  let parsedCodeExpirationMinutes = codeExpirationMinutes;
  let parsedIsAuthTemplate = isAuthTemplate || data?.category === 'AUTHENTICATION';
  let parsedOtpType = otpType;
  let parsedOtpButtonText = otpButtonText;
  let parsedAddSecurityRecommendation = addSecurityRecommendation;

  if (components) {
    try {
      const comps = typeof components === 'string' ? JSON.parse(components) : components;
      const getComp = (type) => {
        if (Array.isArray(comps)) return comps.find(c => c.type === type);
        if (comps && comps[type]) {
          return typeof comps[type] === 'string' ? JSON.parse(comps[type]) : comps[type];
        }
        return null;
      };

      const bodyComp = getComp('BODY');
      if (bodyComp) {
        if (!parsedBodyText && bodyComp.text) parsedBodyText = bodyComp.text;
        if (bodyComp.add_security_recommendation !== undefined) {
          parsedAddSecurityRecommendation = bodyComp.add_security_recommendation;
        }
      }

      const footerComp = getComp('FOOTER');
      if (footerComp?.code_expiration_minutes) {
        parsedCodeExpirationMinutes = footerComp.code_expiration_minutes;
      }
      
      const buttonsComp = getComp('BUTTONS');
      if (buttonsComp?.buttons?.length > 0) {
         const otpBtn = buttonsComp.buttons.find(b => b.type === 'otp' || b.type === 'COPY_CODE');
         if (otpBtn) {
            parsedOtpType = otpBtn.otp_type || 'copy_code';
            parsedOtpButtonText = otpBtn.text;
         }
      }
    } catch (e) {
      // ignore
    }
  }

  const buttons = parseButtons(data, components);
  const effectiveMediaType = mediaType !== 'none' ? mediaType : getMediaTypeFromComponents(components);



  return (
    <div className="sticky top-6 flex flex-col items-center">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4 text-center">Live Template Preview</h3>

      <div 
        className="bg-black rounded-[2.5rem] sm:rounded-[3rem] p-2.5 sm:p-3 shadow-2xl border-4 border-gray-700 dark:border-gray-600 relative overflow-hidden"
        style={{ height: 'min(75vh, 650px)', aspectRatio: '32/55' }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-5 sm:h-6 w-24 sm:w-32 bg-black rounded-b-xl sm:rounded-b-2xl z-20"></div>

        <div className="w-full h-full bg-[#E5DDD5] dark:bg-[#111827] rounded-[2.2rem] overflow-hidden flex flex-col relative">
          <div className="bg-[#075E54] dark:bg-[#1f2c34] p-4 pt-10 text-white flex items-center gap-3 shadow-sm z-10">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs">P</div>
            <div className="flex-1">
              <p className="font-semibold text-sm leading-none">{displayName}</p>
              <p className="text-[10px] opacity-80 mt-0.5">{displayPhoneNumber}</p>
            </div>
          </div>

          <div className="flex-1 p-4 overflow-y-auto bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat opacity-90 dark:opacity-30 dark:bg-gray-900">
            <div className="flex justify-start">
              {(() => {
                const showAuth = parsedIsAuthTemplate && parsedOtpType === 'copy_code';
                if (showAuth) {
                  return (
                    <AuthTemplateContent
                      currentTime={currentTime}
                      codeExpirationMinutes={parsedCodeExpirationMinutes}
                      otpButtonText={parsedOtpButtonText}
                      addSecurityRecommendation={parsedAddSecurityRecommendation}
                      buttons={buttons}
                      otpType={parsedOtpType}
                      code={code}
                      bodyText={parsedBodyText}
                      language={data?.language || 'en'}
                    />
                  );
                }
                return (
                  <RegularTemplateContent
                    headerType={headerType}
                    headerText={headerText}
                    effectiveMediaType={effectiveMediaType}
                    mediaPreview={mediaPreview}
                    bodyText={parsedBodyText}
                    footerText={footerText}
                    buttons={buttons}
                    currentTime={currentTime}
                    sampleValues={sampleValues}
                    mode={mode}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};